import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtErr, mediaUrl } from "../../lib/api";
import { fmtPubDate } from "../OltreLeCarte";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Upload } from "lucide-react";

const EMPTY = { title: "", text: "", image_url: "", published_at: "", media: [], published: true };

export default function AdminPosts() {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [params] = useSearchParams();
  const fileInput = useRef(null);
  const uploadTarget = useRef(null);

  const load = () => api.get("/admin/posts").then((r) => setPosts(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  useEffect(() => { if (params.get("new") === "1") setForm({ ...EMPTY }); }, []);

  useEffect(() => {
    const editId = params.get("edit");
    if (editId && posts.length) {
      const p = posts.find((x) => x.id === editId);
      if (p) setForm({ id: p.id, title: p.title, text: p.text, image_url: p.image_url || "", published_at: p.published_at, media: p.media || [], published: p.published !== false });
    }
  }, [posts]);

  const pickFile = (accept, cb) => {
    uploadTarget.current = cb;
    fileInput.current.accept = accept;
    fileInput.current.click();
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    const cb = uploadTarget.current;
    uploadTarget.current = null;
    if (!f || !cb) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      cb(r.data.url);
      toast.success("File caricato e salvato.");
    } catch (err) {
      toast.error(fmtErr(err, "Caricamento non riuscito."));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (form.id) {
        await api.put(`/admin/posts/${form.id}`, form);
        toast.success(form.published ? "Contenuto aggiornato." : "Bozza salvata.");
      } else {
        await api.post("/admin/posts", form);
        toast.success(form.published ? "Contenuto pubblicato." : "Bozza salvata.");
      }
      setForm(null);
      load();
    } catch (err) {
      toast.error(fmtErr(err, "Salvataggio non riuscito."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare definitivamente questo contenuto?")) return;
    try {
      await api.delete(`/admin/posts/${id}`);
      toast.success("Contenuto eliminato.");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div data-testid="admin-posts">
      <input ref={fileInput} type="file" className="hidden" onChange={onFile} data-testid="post-file-input" />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl text-amber-100">Oltre le Carte</h2>
          <p className="text-xs text-purple-300/60 mt-1">Contenuti editoriali visibili a tutti i visitatori, aggiornabili in qualsiasi momento.</p>
        </div>
        {!form && (
          <button onClick={() => setForm({ ...EMPTY })} data-testid="post-new-btn" className="gold-btn px-5 py-2 text-xs flex items-center gap-2 shrink-0">
            <Plus className="w-4 h-4" /> Scrivi un post
          </button>
        )}
      </div>

      {form && (
        <form onSubmit={save} className="mystic-card p-6 mb-8 space-y-4" data-testid="post-form">
          <div>
            <label className="block text-xs text-purple-300/70 mb-1.5">Titolo *</label>
            <input
              required
              maxLength={200}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="post-title-input"
              className="input-dark"
              placeholder="Titolo del contenuto"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-purple-300/70 mb-1.5">Data di pubblicazione</label>
              <input
                type="date"
                value={form.published_at}
                onChange={(e) => setForm({ ...form, published_at: e.target.value })}
                data-testid="post-date-input"
                className="input-dark"
              />
            </div>
            <div>
              <label className="block text-xs text-purple-300/70 mb-1.5">Immagine di copertina</label>
              <button
                type="button"
                onClick={() => pickFile("image/*", (url) => setForm((f) => ({ ...f, image_url: url })))}
                disabled={uploading}
                data-testid="post-image-upload"
                className="gold-btn w-full px-4 py-2.5 text-xs flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Upload className="w-3.5 h-3.5" /> {uploading ? "Caricamento…" : "Carica foto dal dispositivo"}
              </button>
              <p className="text-[10px] text-purple-300/50 mt-1.5">Carica direttamente dal tuo dispositivo: non serve nessun link.</p>
              {form.image_url && (
                <div className="mt-2 flex items-center gap-2" data-testid="post-image-preview-wrap">
                  <img src={mediaUrl(form.image_url)} alt="Anteprima copertina" className="h-20 rounded-lg border border-[#4a2175] object-cover" data-testid="post-image-preview" />
                  <button type="button" onClick={() => setForm({ ...form, image_url: "" })} data-testid="post-image-remove" className="p-2 text-purple-300/60 hover:text-red-300 transition-colors" title="Rimuovi immagine">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <input
                maxLength={500}
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                data-testid="post-image-input"
                className="input-dark mt-2"
                placeholder="oppure incolla un link https://… (secondario)"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-purple-300/70 mb-1.5">Foto e video aggiuntivi</label>
            <p className="text-[10px] text-purple-300/50 mb-2">Carica i file direttamente dal dispositivo; il link esterno è solo un'opzione secondaria.</p>
            {form.media.map((m, i) => (
              <div key={i} className="mb-2">
                <div className="flex gap-2 items-center">
                <select
                  value={m.type}
                  onChange={(e) => setForm({ ...form, media: form.media.map((x, j) => j === i ? { ...x, type: e.target.value } : x) })}
                  data-testid={`post-media-type-${i}`}
                  className="input-dark shrink-0"
                  style={{ width: "7.5rem" }}
                >
                  <option value="image">Foto</option>
                  <option value="video">Video</option>
                </select>
                <button
                  type="button"
                  onClick={() => pickFile(m.type === "video" ? "video/*" : "image/*", (url) => setForm((f) => ({ ...f, media: f.media.map((x, j) => j === i ? { ...x, url } : x) })))}
                  disabled={uploading}
                  data-testid={`post-media-upload-${i}`}
                  className="shrink-0 px-3 py-2.5 rounded-lg border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition-colors disabled:opacity-60 text-xs flex items-center gap-1.5"
                  title="Carica file dal dispositivo"
                >
                  <Upload className="w-4 h-4" /> Carica
                </button>
                <input
                  maxLength={500}
                  value={m.url}
                  onChange={(e) => setForm({ ...form, media: form.media.map((x, j) => j === i ? { ...x, url: e.target.value } : x) })}
                  data-testid={`post-media-url-${i}`}
                  className="input-dark flex-1"
                  placeholder="oppure incolla un link (YouTube o URL diretto)"
                />
                <button
                  type="button"
                  onClick={() => setForm({ ...form, media: form.media.filter((_, j) => j !== i) })}
                  data-testid={`post-media-remove-${i}`}
                  className="p-2 text-purple-300/60 hover:text-red-300 transition-colors shrink-0"
                  title="Rimuovi"
                >
                  <X className="w-4 h-4" />
                </button>
                </div>
                {m.url && (
                  m.type === "video" ? (
                    <video src={mediaUrl(m.url)} controls className="mt-2 h-28 rounded-lg border border-[#4a2175]" data-testid={`post-media-preview-${i}`} />
                  ) : (
                    <img src={mediaUrl(m.url)} alt="Anteprima media" className="mt-2 h-20 rounded-lg border border-[#4a2175] object-cover" data-testid={`post-media-preview-${i}`} />
                  )
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => setForm({ ...form, media: [...form.media, { type: "image", url: "" }] })}
              data-testid="post-media-add"
              className="text-xs text-amber-300/80 hover:text-amber-200 transition-colors"
            >
              + Aggiungi foto o video
            </button>
          </div>
          <div>
            <label className="block text-xs text-purple-300/70 mb-1.5">Testo *</label>
            <textarea
              required
              rows={8}
              maxLength={20000}
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              data-testid="post-text-input"
              className="input-dark"
              placeholder="Curiosità, pensieri, riflessioni, racconti…"
            />
          </div>
          <label className="flex items-center gap-2.5 text-xs text-purple-200/80 cursor-pointer select-none" data-testid="post-draft-toggle">
            <input
              type="checkbox"
              checked={!form.published}
              onChange={(e) => setForm({ ...form, published: !e.target.checked })}
              className="w-4 h-4 accent-amber-400"
            />
            Salva come bozza (non visibile pubblicamente)
          </label>
          <div className="flex items-center gap-4">
            <button type="submit" disabled={busy || uploading} data-testid="post-save-btn" className="gold-btn px-6 py-2.5 text-xs disabled:opacity-60">
              {busy ? "Salvataggio…" : form.id ? "Salva modifiche" : form.published ? "Pubblica" : "Salva bozza"}
            </button>
            <button type="button" onClick={() => setForm(null)} data-testid="post-cancel-btn" className="text-xs text-purple-300/70 hover:text-purple-200">
              Annulla
            </button>
          </div>
        </form>
      )}

      {posts.length === 0 ? (
        <p className="text-purple-300/50 text-sm" data-testid="posts-empty">Nessun contenuto pubblicato.</p>
      ) : (
        <div className="space-y-3" data-testid="posts-list">
          {posts.map((p) => (
            <div key={p.id} className="mystic-card p-4 flex items-center gap-4" data-testid={`admin-post-${p.id}`}>
              {p.image_url && <img src={mediaUrl(p.image_url)} alt="" className="w-14 h-14 rounded-lg object-cover border border-[#4a2175] shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-amber-100 font-medium truncate">{p.title}</p>
                <p className="text-xs text-purple-300/50 mt-0.5">
                  {fmtPubDate(p.published_at)}
                  {p.published === false && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-purple-400/70 border border-[#4a2175] rounded-full px-2 py-0.5" data-testid={`post-draft-badge-${p.id}`}>Bozza</span>
                  )}
                  {(p.likes ?? 0) > 0 && ` · ${p.likes} mi piace`}
                </p>
              </div>
              <button
                onClick={() => setForm({ id: p.id, title: p.title, text: p.text, image_url: p.image_url || "", published_at: p.published_at, media: p.media || [], published: p.published !== false })}
                data-testid={`post-edit-${p.id}`}
                className="p-2 text-purple-300/70 hover:text-amber-300 transition-colors"
                title="Modifica"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => remove(p.id)}
                data-testid={`post-delete-${p.id}`}
                className="p-2 text-purple-300/70 hover:text-red-300 transition-colors"
                title="Elimina"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
